import {
	MarketBasketStatus,
	MarketBasketSummary,
	Notification,
	PushEvent,
	User,
	UserSummary,
} from '@domain'
import { MarketBasketsKeys } from '@services/marketBaskets.api'
import { NotificationsKeys } from '@services/notifications.api'
import { UsersKey } from '@services/users.api'
import { QueryClient } from '@tanstack/react-query'

// Maps an event `type` to the cache change it implies — a registry, not a switch, so
// a new event is one entry here.

type PushEventPayloads = {
	basketPurchases: { type: string; payload: string } // example only
	marketBasketSummaryUpdated: {
		id: Id
		created: boolean
		name: string | null
		displayName: string | null
		salesforceCaseNumber: string | null
		salesforceCaseId: string | null
		bepBrand: string | null
		status: string
		processingStatus: string | null
		progress: number | null
		analysisError: string | null
		analysisWarning: string | null
		effectiveDate: string
		dateRangeStart: string
		dateRangeEnd: string
		createdAt: string
		deleted: boolean
		assignedAnalyst: UserSummary | null
		createdByDisplayName: string | null
		tier: string | null
		basketType: string | null
		sourceType: string | null
	}
	marketBasketDeleted: {
		id: Id
	}
	notificationReceived: Notification
	notificationUnreadCountChanged: {
		unreadCount: number
	}
}

type CacheHandler<T extends keyof PushEventPayloads> = (
	payload: PushEventPayloads[T],
	queryClient: QueryClient
) => void

type PushEventHandler = {
	[T in keyof PushEventPayloads]?: CacheHandler<T>
}

// TODO (rl) fix how the handler and queryClient work.

/**
 * pushEventHandlers
 * Handles an incoming event
 * The key is the event type, the value the action it creates, typically a cache update or invalidation.
 * @example
 * purchases.progress: ({ basketId }, queryClient) => {
 * 		queryClient.invalidateQueries({
 * 			queryKey: [...MarketBasketsKeys.marketBasket(basketId), 'purchases'],
 * 		})
 * 	},
 */
export const pushEventHandlers: PushEventHandler = {
	basketPurchases: (event: PushEvent, queryClient: QueryClient) => {
		// example only
		queryClient.invalidateQueries({
			queryKey: [...MarketBasketsKeys.marketBasket(1), 'purchases'],
		})
	},

	marketBasketSummaryUpdated: (payload, queryClient) => {
		if (payload.created) {
			queryClient.invalidateQueries({ queryKey: MarketBasketsKeys.all })
			return
		}

		const { status, ...rest } = payload

		queryClient.setQueriesData<PaginatedAPIRes<MarketBasketSummary[]>>(
			{ queryKey: MarketBasketsKeys.all },
			(old) =>
				old?.data
					? {
							...old,
							data: old.data.map((basket) => {
								if (basket.id !== rest.id) return basket

								const nextStatus = (Object.values(MarketBasketStatus) as string[]).includes(status)
									? (status as MarketBasketStatus)
									: basket.status

								return {
									...basket,
									...rest,
									status: nextStatus,
									name: rest.name ?? basket.name,
									displayName: rest.displayName ?? basket.displayName,
									salesforceCaseNumber: rest.salesforceCaseNumber ?? basket.salesforceCaseNumber,
									salesforceCaseId: rest.salesforceCaseId ?? basket.salesforceCaseId,
									bepBrand: rest.bepBrand ?? basket.bepBrand,
									processingStatus: rest.processingStatus ?? basket.processingStatus,
									assignedAnalyst: rest.assignedAnalyst ?? basket.assignedAnalyst,
									createdByDisplayName: rest.createdByDisplayName ?? basket.createdByDisplayName,
									tier: rest.tier ?? basket.tier,
									basketType: rest.basketType ?? basket.basketType,
									sourceType: rest.sourceType ?? basket.sourceType,
								}
							}),
						}
					: old
		)
	},

	marketBasketDeleted: (_, queryClient) => {
		queryClient.invalidateQueries({ queryKey: MarketBasketsKeys.all })
	},

	notificationReceived: (_, queryClient) => {
		queryClient.invalidateQueries({ queryKey: NotificationsKeys.all })
	},

	notificationUnreadCountChanged: (payload, queryClient) => {
		queryClient.setQueryData<User>(UsersKey.me, (old) =>
			old ? { ...old, unreadNotificationCount: payload.unreadCount } : old
		)
	},
}

export function runPushEventHandler({
	event,
	queryClient,
}: {
	event: PushEvent
	queryClient: QueryClient
}) {
	const handler = pushEventHandlers[event.type as keyof PushEventPayloads] as
		| ((payload: unknown, queryClient: QueryClient) => void)
		| undefined
	handler?.(event.payload, queryClient)
}
